import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { format, isValid, parseISO } from 'date-fns';
import { stringToDate } from './date-presentation.ts';

export {
  dateFnsLocalesMap,
  hasTimeComponent,
  toDisplayDate,
  getDateWithUTCOffset,
  getDateFromISOString,
  stringToDate
} from './date-presentation.ts';

import { encodeHtml } from '../../utils.ts';
import type { FieldType } from '../field-types.ts';

import { DATE_VALUE_TODAY, DATETIME_FORMAT, parseDate } from './date-normalization.ts';

export {
  DATE_VALUE_TODAY,
  DATE_FORMAT,
  DATETIME_FORMAT,
  isValidDateString,
  parseDate,
  parseDatetime
} from './date-normalization.ts';

/** Extracts calendar date (YYYY-MM-DD) in the local timezone from an ISO string. */
export function toCalendarDate(isoString: string): string {
  const date = parseISO(isoString);
  if (isNaN(date.getTime())) {
    return isoString.slice(0, 10);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function normalizeDatetimeString(value: string): string {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, DATETIME_FORMAT) : value;
}

export function getLocalizedDatePlaceholder(locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  }).format(new Date());
}

function getEncodedDate(value: string): string {
  return `<time datetime="${encodeHtml(value)}">${encodeHtml(value)}</time>`;
}

const dateFieldType: FieldType<DatabaseFieldType.DATE> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.LESS
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value }) => {
    const parsed = parseDate(value);
    if (parsed) {
      return getEncodedDate(parsed);
    } else {
      return '';
    }
  },
  parseFromHtml: (el) =>
    stringToDate(
      el.find('time').eq(0).attr('datetime') ??
        el.find('.date-node').eq(0).attr('data-timestamp') ??
        el.text().trim()
    ),

  getInitialValue: () => undefined,

  getInitialDefaultValue: () => DATE_VALUE_TODAY,

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    const parsed1 = parseDate(value1);
    const parsed2 = parseDate(value2);
    if (parsed1 === undefined || parsed2 === undefined) {
      return parsed1 === parsed2;
    }
    // Compare by calendar date only (YYYY-MM-DD) to correctly handle mixed
    // date-only and datetime values on the same calendar day.
    // Using parseISO timestamp comparison would fail because date-only strings
    // parse to midnight local time, which differs from any datetime on that day.
    return parsed1.slice(0, 10) === parsed2.slice(0, 10);
  },

  getStringRepresentation: ({ value }) => parseDate(value) ?? '',

  getNumberRepresentation: ({ value }) => {
    const date = parseDate(value);
    return date ? parseISO(date).getTime() : undefined;
  },

  getFilterNumberRepresentation: ({ value }) => {
    const date = parseDate(value);
    if (!date) {
      return undefined;
    }
    return parseISO(toCalendarDate(date)).getTime();
  },

  convertFromCsv: ({ value }) => {
    return stringToDate(value.plain);
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => stringToDate(value.plain)
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }) => ({
      plain: parseDate(value) ?? ''
    })
  }
};

export default dateFieldType;
